import type { JobClickTrackingSelectedJobAttributes } from '../../../../context/ViewJobDataContext';

import { isJobFromHomePageWeb, isJobFromSerpWeb } from '../../../../utils/fromParamUtils';

/**
 * Web implementation of legacy auto-open job-click tracking, ported from
 * jobsearch-ui `jasxJobClick.tsx` (`triggerJobClickTracking` /
 * `triggerRawClickLogging`). Fired from the RNVJ ApplyButton when the viewjob was
 * auto-opened in a desktop two-pane feed (the user never clicked the job card),
 * so the job click is counted at apply time.
 *
 * Native is a no-op (see `jobClickTracking.ts`).
 */

/**
 * Extra rawclick query params, matching the host `/m/rpc/rawclick` contract
 * (jobsearch-ui `getRawClickLogginURL`). Booleans are serialized as `'1'`/`'0'`.
 */
export type RawClickOptions = {
    surface?: string;
    ia?: boolean;
    isFirstAutoLoad?: boolean;
    isApplyStartClick?: boolean;
};

/**
 * Minimal attributes needed to build a rawclick payload. `sponsored` (an explicit
 * boolean, e.g. from the VJ model) takes precedence; otherwise `adId` presence
 * implies sponsored (auto-open job-click path carries `adId`).
 */
export type RawClickAttrs = {
    tk?: string;
    jobKey?: string;
    adId?: string;
    sponsored?: boolean;
};

/**
 * Resolve the rawclick `surface` to one of: 'viewjob', 'homepage', 'serp'.
 *
 * Standalone viewjob (mobile/desktop) is always 'viewjob'. An embedded VJ pane
 * (desktop two-pane) is attributed to its host page via the `from` URL param,
 * which the host already sets on the viewjob link (e.g. `from=hp` / `mobhp_*` for
 * homepage, `from=serp` / `tp-serp` for SERP). `from` is read from the VJ context
 * (see `useViewJobDataContext().from`); mirrors `useFullScreenVJActive`.
 */
export function resolveRawClickSurface(
    from: string | undefined | null,
    viewJobDisplay: string,
): string {
    if (viewJobDisplay === 'MOBILE_STANDALONE' || viewJobDisplay === 'DESKTOP_STANDALONE') {
        return 'viewjob';
    }
    if (isJobFromHomePageWeb(from)) {
        return 'homepage';
    }
    if (isJobFromSerpWeb(from)) {
        return 'serp';
    }
    return 'viewjob';
}

function createImgBeacon(url: string): void {
    const imgBeacon = new globalThis.Image();
    imgBeacon.src = url;
}

/**
 * Pushes a history entry updating `vjk`/`advn` to reflect the selected job,
 * mirroring legacy `generateNewHistoryUrlParams` + `pushState`.
 */
function pushSelectedJobHistory(
    selectedJobAttributes: JobClickTrackingSelectedJobAttributes,
): void {
    try {
        const currentUrl = new URL(window.location.href);
        const params = currentUrl.searchParams;
        if (selectedJobAttributes.advn) {
            params.set('advn', selectedJobAttributes.advn);
        } else {
            params.delete('advn');
        }
        if (selectedJobAttributes.jobKey) {
            params.set('vjk', selectedJobAttributes.jobKey);
        }
        params.delete('indconvtk');
        window.history.pushState(null, '', currentUrl.href);
    } catch (error) {
        console.error('[jobClickTracking] Failed to push selected job history:', error);
    }
}

/**
 * Builds the internal click-tracking URL: adds `vjs=2`, `autoopen=1`, and a
 * cache-busting `__` param. Mirrors legacy `getInternalTrackingURL`. This util
 * is only invoked for auto-opened jobs, so `autoopen=1` is always set.
 */
function getInternalTrackingURL(internalTrackUrl: URL): string {
    internalTrackUrl.searchParams.set('vjs', '2');
    internalTrackUrl.searchParams.set('autoopen', '1');
    internalTrackUrl.searchParams.append('__', Math.random().toString());

    return internalTrackUrl.href;
}

function triggerExternalTracking(trackingUrls: string[] | null | undefined): void {
    if (!trackingUrls) {
        return;
    }
    trackingUrls.forEach((url: string) => {
        try {
            const externalTrackingUrl = new URL(url);
            externalTrackingUrl.searchParams.append('__', Math.random().toString());
            createImgBeacon(externalTrackingUrl.href);
        } catch (error) {
            console.error('[jobClickTracking] Failed to fire external tracking beacon:', error);
        }
    });
}

export function triggerJobClickTracking(
    selectedJobAttributes: JobClickTrackingSelectedJobAttributes | undefined,
): void {
    if (!selectedJobAttributes || !selectedJobAttributes.link) {
        return;
    }
    pushSelectedJobHistory(selectedJobAttributes);
    try {
        const baseInternalTrackingUrl = new URL(
            selectedJobAttributes.link,
            window.location.origin,
        );
        createImgBeacon(getInternalTrackingURL(baseInternalTrackingUrl));
    } catch (error) {
        console.error('[jobClickTracking] Failed to fire internal tracking beacon:', error);
    }
    triggerExternalTracking(selectedJobAttributes.extractTrackingUrls);
}

export function triggerRawClickLogging(
    attrs: RawClickAttrs | undefined,
    isLoggedIn: boolean,
    options: RawClickOptions = {},
): void {
    if (!attrs) {
        return;
    }
    try {
        const isSponsored = attrs.sponsored ?? !!attrs.adId;
        const rawClickLoggingURL = new URL('/m/rpc/rawclick', window.location.origin);
        rawClickLoggingURL.searchParams.set('tk', attrs.tk ?? '');
        rawClickLoggingURL.searchParams.set('jobKey', attrs.jobKey ?? '');
        rawClickLoggingURL.searchParams.set('sponsored', isSponsored ? '1' : '0');
        rawClickLoggingURL.searchParams.set('surface', options.surface ?? '');
        rawClickLoggingURL.searchParams.set('ia', options.ia ? '1' : '0');
        rawClickLoggingURL.searchParams.set('isFirstAutoLoad', options.isFirstAutoLoad ? '1' : '0');
        rawClickLoggingURL.searchParams.set(
            'isApplyStartClick',
            options.isApplyStartClick ? '1' : '0',
        );
        rawClickLoggingURL.searchParams.set('loggedIn', isLoggedIn ? '1' : '0');
        void fetch(rawClickLoggingURL.toString());
    } catch (error) {
        console.error('[jobClickTracking] Failed to fire raw click logging:', error);
    }
}
